import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../../../lib/format";

// One open invoice with its client's details, as returned by
// GET /api/collection/estimates?status=open — used for the Excel export.
interface OpenInvoice extends Invoice {
  client_name: string;
  client_contact: string | null;
  client_email: string | null;
  client_rate: number | null;
  client_terms: number;
}

// Add `days` calendar days to a YYYY-MM-DD date, returning YYYY-MM-DD.
function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

interface ClientRow {
  id: number; name: string; contact_name: string | null; email: string | null;
  language: "en" | "th"; credit_terms_days: number; notes: string | null;
  rate: number | null;
  open_count: number; open_total: number; open_sms: number;
  ignored_count: number; ignored_total: number;
  last_sent_at: string | null;
  invoice_search: string | null;
}

type ViewFilter = "all" | "debt" | "quotation";

interface Invoice {
  id: number;
  number: string;
  issue_date: string;
  taxable_amount: number;  // before tax
  tax_amount: number;      // VAT
  amount: number;          // tax-inclusive total — the figure we chase everywhere
  status: string;
  memo: string | null;
  collection_status: "open" | "paid" | "written_off" | "ignored";
  sms_override: number | null;
  marked_paid_at: string | null;
}

// Invoice amounts include 7% VAT; strip it before dividing by the per-SMS rate.
const VAT_DIVISOR = 1.07;

const round2 = (n: number) => Math.round(n * 100) / 100;

// The Amount column is tax-inclusive everywhere in this app. For the export we split it out.
// Imported invoices carry the real split from the source file, so use that whenever it
// reconciles; manually added invoices don't (they store tax_amount = 0), so fall back to the
// same 7% VAT the SMS calculation already assumes. Either way Before tax + Tax === Amount.
function beforeTax(inv: Invoice): number {
  if (!inv.amount) return 0;
  const before = inv.taxable_amount ?? 0;
  const tax = inv.tax_amount ?? 0;
  if (tax > 0 && Math.abs(before + tax - inv.amount) < 0.02) return round2(before);
  return round2(inv.amount / VAT_DIVISOR);
}

function taxOf(inv: Invoice): number {
  return round2(inv.amount - beforeTax(inv));
}

// SMS for an invoice: manual override if set, else ROUND((amount / 1.07) / rate). null = unknown (no rate).
function smsFor(amount: number, rate: number | null | undefined, override: number | null): number | null {
  if (override != null) return override;
  if (rate && rate > 0) return Math.round((amount / VAT_DIVISOR) / rate);
  return null;
}

export default function Clients() {
  const { current } = useCurrentCompany();
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState<ViewFilter>("all");
  const [editing, setEditing] = useState<ClientRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  async function refresh() {
    if (!current) return;
    const r = await api<{ clients: ClientRow[] }>(`/api/collection/clients?company_id=${current.id}`);
    setRows(r.clients);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [current?.id]);

  // Export the current company's invoices to an .xlsx file: a sheet of open
  // invoices (real debt) and a sheet of quotations (ignored), each with its own
  // totals row. xlsx is imported lazily to keep it out of the main bundle.
  async function exportOpenInvoices() {
    if (!current || exporting) return;
    setExporting(true);
    try {
      const [openRes, quoteRes] = await Promise.all([
        api<{ estimates: OpenInvoice[] }>(`/api/collection/estimates?company_id=${current.id}&status=open`),
        api<{ estimates: OpenInvoice[] }>(`/api/collection/estimates?company_id=${current.id}&status=ignored`),
      ]);
      const open = openRes.estimates;
      const quotes = quoteRes.estimates;
      if (!open.length && !quotes.length) {
        alert("No invoices to export.");
        return;
      }

      const XLSX = await import("xlsx");
      const header = [
        "Client", "Contact", "Email", "Invoice No.",
        "Issue Date", "Due Date", "Before tax", "Tax", "Amount", "SMS", "Memo",
      ];
      // Column indexes of the three money columns, used for number formatting and the totals row.
      const MONEY_COLS = [6, 7, 8];
      // Build one worksheet (header + one row per invoice + a totals row) from a list.
      const sheetFor = (invoices: OpenInvoice[]) => {
        const body = invoices.map((inv) => {
          const sms = smsFor(inv.amount, inv.client_rate, inv.sms_override);
          return [
            inv.client_name,
            inv.client_contact || "",
            inv.client_email || "",
            inv.number,
            inv.issue_date,
            addDays(inv.issue_date, inv.client_terms),
            beforeTax(inv),
            taxOf(inv),
            inv.amount,
            sms ?? "",
            inv.memo || "",
          ];
        });
        const totalBeforeTax = invoices.reduce((s, i) => s + beforeTax(i), 0);
        const totalTax = invoices.reduce((s, i) => s + taxOf(i), 0);
        const totalAmount = invoices.reduce((s, i) => s + i.amount, 0);
        const totalSms = invoices.reduce((s, i) => s + (smsFor(i.amount, i.client_rate, i.sms_override) ?? 0), 0);
        const totalsRow = [
          "", "", "", "", "", `Total (${invoices.length})`,
          totalBeforeTax, totalTax, totalAmount, totalSms, "",
        ];
        const ws = XLSX.utils.aoa_to_sheet([header, ...body, totalsRow]);
        // Format the money columns (Before tax / Tax / Amount).
        for (let i = 1; i <= body.length + 1; i++) {
          for (const c of MONEY_COLS) {
            const cell = ws[XLSX.utils.encode_cell({ r: i, c })];
            if (cell && typeof cell.v === "number") cell.z = "#,##0.00";
          }
        }
        ws["!cols"] = [
          { wch: 26 }, { wch: 18 }, { wch: 28 }, { wch: 12 },
          { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 8 }, { wch: 40 },
        ];
        return ws;
      };

      const wb = XLSX.utils.book_new();
      // Always include both sheets (even if empty) so the file layout is predictable.
      XLSX.utils.book_append_sheet(wb, sheetFor(open), "Open invoices");
      XLSX.utils.book_append_sheet(wb, sheetFor(quotes), "Quotations");
      const today = new Date().toISOString().slice(0, 10);
      const safeName = (current.name || "company").replace(/[^\w.-]+/g, "_");
      XLSX.writeFile(wb, `invoices-${safeName}-${today}.xlsx`);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Could not export invoices.");
    } finally {
      setExporting(false);
    }
  }

  // Company-wide totals for the summary cards.
  const sums = rows.reduce(
    (a, c) => {
      a.openTotal += c.open_total; a.openCount += c.open_count; a.openSms += c.open_sms || 0;
      a.ignTotal += c.ignored_total; a.ignCount += c.ignored_count;
      if (c.open_count > 0) a.openClients++;
      if (c.ignored_count > 0) a.ignClients++;
      return a;
    },
    { openTotal: 0, openCount: 0, openSms: 0, ignTotal: 0, ignCount: 0, openClients: 0, ignClients: 0 },
  );

  const needle = q.trim().toLowerCase();
  const filtered = rows.filter((c) => {
    // Real debt (open) vs Quotation only (ignored) filter.
    if (view === "debt" && c.open_count === 0) return false;
    if (view === "quotation" && c.ignored_count === 0) return false;
    if (!needle) return true;
    // name, email, plus invoice numbers + amounts (e.g. "1042" or "1500")
    const hay = `${c.name} ${c.email || ""} ${c.invoice_search || ""}`.toLowerCase();
    if (hay.includes(needle)) return true;
    // let "1,500" match an amount stored as 1500
    const numNeedle = needle.replace(/,/g, "");
    return numNeedle !== needle && hay.includes(numNeedle);
  });

  const currency = current?.currency || "THB";

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">{rows.length} total · {rows.filter((r) => r.open_count > 0).length} with open balance</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary" onClick={exportOpenInvoices} disabled={exporting || !current}
                  title="Download an Excel file: Open invoices + Quotations (separate sheets)">
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 2a.75.75 0 01.75.75v8.19l2.72-2.72a.75.75 0 111.06 1.06l-4 4a.75.75 0 01-1.06 0l-4-4a.75.75 0 111.06-1.06l2.72 2.72V2.75A.75.75 0 0110 2zM3.75 14a.75.75 0 01.75.75v1.5c0 .414.336.75.75.75h9.5a.75.75 0 00.75-.75v-1.5a.75.75 0 011.5 0v1.5A2.25 2.25 0 0115.25 18.5h-9.5A2.25 2.25 0 013.5 16.25v-1.5a.75.75 0 01.75-.75z" clipRule="evenodd" /></svg>
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
          <button className="btn-primary" onClick={() => setAdding(true)}>
            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" /></svg>
            New client
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FilterCard
          label="Real debt" sub={`${sums.openCount} open · ${sums.openClients} clients`}
          value={fmtMoney(sums.openTotal, currency)} accent="from-violet-50 to-white"
          active={view === "debt"} onClick={() => setView(view === "debt" ? "all" : "debt")}
        />
        <FilterCard
          label="Quotation only (ignored)" sub={`${sums.ignCount} quotes · ${sums.ignClients} clients`}
          value={fmtMoney(sums.ignTotal, currency)} accent="from-amber-50 to-white"
          active={view === "quotation"} onClick={() => setView(view === "quotation" ? "all" : "quotation")}
        />
        <FilterCard
          label="Total SMS (debt)" sub="from open invoices"
          value={sums.openSms.toLocaleString()} accent="from-emerald-50 to-white"
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="relative w-96">
          <svg className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>
          <input className="search-input w-full" placeholder="Search by name, email, invoice no. or amount…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {view !== "all" && (
          <button className="btn-secondary !py-1.5 !px-3 text-xs" onClick={() => setView("all")}>
            {view === "debt" ? "Showing: Real debt" : "Showing: Quotations"} · Clear ✕
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="refined">
          <thead>
            <tr>
              <th className="w-8 pl-3 pr-0"></th>
              <th>Client</th>
              <th>Email</th>
              <th>Lang</th>
              <th>Terms</th>
              <th>Open</th>
              <th className="text-right">Open total</th>
              <th className="text-right">Total SMS</th>
              <th>Last sent</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <ClientRowView
                key={c.id}
                client={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                onEdit={() => setEditing(c)}
                onChanged={refresh}
                currency={currency}
                defaultShowAll={view === "quotation"}
              />
            ))}
            {!filtered.length && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-500">
                {rows.length
                  ? view === "quotation" ? "No clients with quotation-only (ignored) invoices."
                  : view === "debt" ? "No clients with open debt."
                  : "No matches"
                  : "No clients yet — import a QBO file or add manually."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(editing || adding) && current && (
        <EditDialog
          companyId={current.id}
          client={editing}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={() => { setEditing(null); setAdding(false); refresh(); }}
        />
      )}
    </div>
  );
}

function ChevronRight({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`}
      viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
    >
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
    </svg>
  );
}

function ClientRowView({
  client, expanded, onToggle, onEdit, onChanged, currency, defaultShowAll,
}: {
  client: ClientRow;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onChanged: () => void;
  currency: string;
  defaultShowAll?: boolean;
}) {
  return (
    <>
      <tr className={expanded ? "bg-slate-50/60" : ""}>
        <td className="pl-3 pr-0">
          <button type="button" onClick={onToggle} className="btn-ghost !p-1.5"
                  aria-label={expanded ? "Collapse" : "Expand"}
                  title={expanded ? "Hide invoices" : "Show invoices"}>
            <ChevronRight open={expanded} />
          </button>
        </td>
        <td>
          <button onClick={onToggle} className="text-left font-medium text-slate-900 hover:text-indigo-700">
            {client.name}
          </button>
          {client.contact_name && <div className="text-xs text-slate-500">{client.contact_name}</div>}
        </td>
        <td className="text-slate-700">
          {client.email
            ? (() => {
                const parts = client.email.split(/[,;]/).map((e) => e.trim()).filter(Boolean);
                return (
                  <div>
                    <div>{parts[0]}</div>
                    {parts.length > 1 && (
                      <div className="text-[11px] text-slate-400">+{parts.length - 1} CC</div>
                    )}
                  </div>
                );
              })()
            : <span className="text-rose-500">— missing —</span>}
        </td>
        <td><span className="pill pill-slate uppercase">{client.language}</span></td>
        <td className="text-slate-600">{client.credit_terms_days}<span className="text-slate-400">d</span></td>
        <td>{client.open_count > 0
          ? <span className="font-medium text-slate-900">{client.open_count}</span>
          : <span className="text-slate-400">0</span>}</td>
        <td className="text-right font-mono font-semibold text-slate-900">
          {client.open_total > 0 ? fmtMoney(client.open_total, currency) : <span className="text-slate-300">—</span>}
        </td>
        <td className="text-right font-mono text-slate-900">
          {client.open_sms > 0
            ? client.open_sms.toLocaleString()
            : client.rate == null
              ? <span className="text-slate-300" title="No rate set — set a rate or a manual SMS count per invoice">—</span>
              : <span className="text-slate-300">0</span>}
        </td>
        <td className="text-xs text-slate-500">{client.last_sent_at ? fmtDate(client.last_sent_at) : "—"}</td>
        <td className="pr-3 text-right">
          <button className="btn-secondary !py-1 !px-2.5 text-xs" onClick={onEdit}>Edit</button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/40">
          <td colSpan={10} className="px-5 py-4">
            <InvoicesPanel clientId={client.id} currency={currency} onChanged={onChanged} defaultShowAll={defaultShowAll} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvoicesPanel({
  clientId, currency, onChanged, defaultShowAll,
}: { clientId: number; currency: string; onChanged: () => void; defaultShowAll?: boolean }) {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(!!defaultShowAll);
  const [busyId, setBusyId] = useState<number | null>(null);
  // Inline row editing: which invoice is being edited + its draft amount / SMS.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftAmount, setDraftAmount] = useState("");
  const [draftSms, setDraftSms] = useState("");
  const [draftMemo, setDraftMemo] = useState("");
  // Inline add-row for a brand-new invoice.
  const [adding, setAdding] = useState(false);
  const blankNew = { number: "", issue_date: new Date().toISOString().slice(0, 10), amount: "", memo: "", sms: "" };
  const [newInv, setNewInv] = useState(blankNew);

  async function load() {
    const r = await api<{ client: { rate: number | null }; estimates: Invoice[] }>(`/api/collection/clients/${clientId}`);
    setRate(r.client?.rate ?? null);
    setInvoices(r.estimates);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  function startEdit(inv: Invoice) {
    setEditingId(inv.id);
    setDraftAmount(String(inv.amount));
    // SMS field holds the manual override only; blank = use the auto value.
    setDraftSms(inv.sms_override != null ? String(inv.sms_override) : "");
    setDraftMemo(inv.memo ?? "");
  }
  function cancelEdit() {
    setEditingId(null);
    setDraftAmount("");
    setDraftSms("");
    setDraftMemo("");
  }
  async function saveEdit(inv: Invoice) {
    const amt = Number(draftAmount.trim().replace(/,/g, ""));
    if (!Number.isFinite(amt) || amt < 0) { alert("Amount must be a non-negative number."); return; }

    const smsTrim = draftSms.trim().replace(/[, ]/g, "");
    let sms_override: number | null;
    if (smsTrim === "") {
      sms_override = null; // blank → auto (computed from amount / rate)
    } else {
      const n = Math.round(Number(smsTrim));
      if (!Number.isFinite(n) || n < 0) { alert("SMS must be a non-negative number, or blank for auto."); return; }
      sms_override = n;
    }

    setBusyId(inv.id);
    try {
      await api(`/api/collection/estimates/${inv.id}`, {
        method: "PATCH",
        body: JSON.stringify({ amount: amt, sms_override, memo: draftMemo.trim() || null }),
      });
      cancelEdit();
      await load();
      onChanged();
    } finally { setBusyId(null); }
  }

  function startAdd() { setEditingId(null); setNewInv(blankNew); setAdding(true); }
  function cancelAdd() { setAdding(false); setNewInv(blankNew); }
  async function saveNew() {
    const number = newInv.number.trim();
    const issue_date = newInv.issue_date.trim();
    const amount = Number(newInv.amount.trim().replace(/,/g, ""));
    if (!number) { alert("Invoice number is required."); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(issue_date)) { alert("Pick a valid date."); return; }
    if (!Number.isFinite(amount) || amount < 0) { alert("Amount must be a non-negative number."); return; }
    const smsTrim = newInv.sms.trim().replace(/[, ]/g, "");
    let sms_override: number | null = null;
    if (smsTrim !== "") {
      const n = Math.round(Number(smsTrim));
      if (!Number.isFinite(n) || n < 0) { alert("SMS must be a non-negative number, or blank for auto."); return; }
      sms_override = n;
    }
    setBusyId(-1);
    try {
      await api("/api/collection/estimates", {
        method: "POST",
        body: JSON.stringify({ client_id: clientId, number, issue_date, amount, memo: newInv.memo.trim() || null, sms_override }),
      });
      cancelAdd();
      await load();
      onChanged();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Could not add the invoice.");
    } finally { setBusyId(null); }
  }

  async function setStatus(id: number, status: Invoice["collection_status"]) {
    setBusyId(id);
    try {
      await api(`/api/collection/estimates/${id}`, { method: "PATCH", body: JSON.stringify({ collection_status: status }) });
      await load();
      onChanged();
    } finally { setBusyId(null); }
  }

  async function remove(id: number, number: string) {
    if (!confirm(`Delete invoice ${number}? This cannot be undone (re-import to restore).`)) return;
    setBusyId(id);
    try {
      await api(`/api/collection/estimates/${id}`, { method: "DELETE" });
      await load();
      onChanged();
    } finally { setBusyId(null); }
  }

  if (invoices === null) return <div className="text-xs text-slate-500">Loading invoices…</div>;

  const visible = showAll ? invoices : invoices.filter((i) => i.collection_status === "open");
  const autoSmsNew = smsFor(Number(newInv.amount.replace(/,/g, "")) || 0, rate, null);

  return (
    <div className="rounded-lg bg-white p-4 shadow-soft ring-1 ring-slate-900/5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="section-title">Invoices <span className="font-normal text-slate-400">({visible.length}/{invoices.length})</span></div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
                   className="h-3.5 w-3.5 rounded border-slate-300" />
            Show closed / quotes too
          </label>
          <button type="button" className="btn-secondary !py-1 !px-2.5 text-xs"
                  onClick={() => (adding ? cancelAdd() : startAdd())}>
            {adding ? "Close" : "+ Add"}
          </button>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="text-left">
          <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-3 font-semibold">Number</th>
            <th className="font-semibold">Date</th>
            <th className="text-right font-semibold">Amount</th>
            <th className="text-right font-semibold">SMS</th>
            <th className="font-semibold">Memo</th>
            <th className="font-semibold">Status</th>
            <th className="text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="border-b border-slate-100 bg-emerald-50/40">
              <td className="py-2 pr-3">
                <input type="text" autoFocus placeholder="Invoice no."
                       className="w-24 rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                       value={newInv.number}
                       onChange={(e) => setNewInv({ ...newInv, number: e.target.value })}
                       onKeyDown={(e) => { if (e.key === "Enter") saveNew(); if (e.key === "Escape") cancelAdd(); }} />
              </td>
              <td>
                <input type="date"
                       className="rounded border border-slate-300 px-2 py-1 text-xs"
                       value={newInv.issue_date}
                       onChange={(e) => setNewInv({ ...newInv, issue_date: e.target.value })} />
              </td>
              <td className="text-right">
                <input type="text" inputMode="decimal" placeholder="Amount"
                       className="w-28 rounded border border-slate-300 px-2 py-1 text-right font-mono text-xs"
                       value={newInv.amount}
                       onChange={(e) => setNewInv({ ...newInv, amount: e.target.value })}
                       onKeyDown={(e) => { if (e.key === "Enter") saveNew(); if (e.key === "Escape") cancelAdd(); }} />
              </td>
              <td className="text-right">
                <input type="text" inputMode="numeric"
                       className="w-24 rounded border border-slate-300 px-2 py-1 text-right font-mono text-xs"
                       value={newInv.sms}
                       placeholder={autoSmsNew != null ? `auto ${autoSmsNew.toLocaleString()}` : "auto"}
                       onChange={(e) => setNewInv({ ...newInv, sms: e.target.value })}
                       onKeyDown={(e) => { if (e.key === "Enter") saveNew(); if (e.key === "Escape") cancelAdd(); }} />
              </td>
              <td>
                <input type="text" placeholder="Memo (optional)"
                       className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                       value={newInv.memo}
                       onChange={(e) => setNewInv({ ...newInv, memo: e.target.value })}
                       onKeyDown={(e) => { if (e.key === "Enter") saveNew(); if (e.key === "Escape") cancelAdd(); }} />
              </td>
              <td><span className="pill pill-blue">open</span></td>
              <td className="space-x-1 pr-1 text-right whitespace-nowrap">
                <button disabled={busyId === -1}
                        className="chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-100"
                        onClick={saveNew} title="Add invoice">Save</button>
                <button disabled={busyId === -1}
                        className="chip bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200"
                        onClick={cancelAdd} title="Cancel">Cancel</button>
              </td>
            </tr>
          )}
          {visible.length === 0 && !adding && (
            <tr><td colSpan={7} className="py-4 text-center text-slate-400">
              {invoices.length === 0 ? "No invoices on file — click “+ Add” to create one." : "No open invoices (tick “Show closed / quotes too”)."}
            </td></tr>
          )}
          {visible.map((inv) => {
            const isEditing = editingId === inv.id;
            // Live auto-SMS hint for the placeholder, based on the amount being typed.
            const autoSms = smsFor(Number(draftAmount.replace(/,/g, "")) || 0, rate, null);
            return (
            <tr key={inv.id} className={`border-b border-slate-100 last:border-0 ${isEditing ? "bg-indigo-50/40" : "hover:bg-slate-50/60"}`}>
              <td className="py-2 pr-3 font-mono text-slate-900">{inv.number}</td>
              <td className="text-slate-700">{fmtDate(inv.issue_date)}</td>
              <td className="text-right font-mono font-semibold text-slate-900">
                {isEditing ? (
                  <input
                    type="text" inputMode="decimal" autoFocus
                    className="w-28 rounded border border-slate-300 px-2 py-1 text-right font-mono text-xs"
                    value={draftAmount}
                    onChange={(e) => setDraftAmount(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(inv); if (e.key === "Escape") cancelEdit(); }}
                  />
                ) : fmtMoney(inv.amount, currency)}
              </td>
              <td className="text-right font-mono text-slate-900">
                {isEditing ? (
                  <input
                    type="text" inputMode="numeric"
                    className="w-24 rounded border border-slate-300 px-2 py-1 text-right font-mono text-xs"
                    value={draftSms}
                    placeholder={autoSms != null ? `auto ${autoSms.toLocaleString()}` : "auto"}
                    onChange={(e) => setDraftSms(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(inv); if (e.key === "Escape") cancelEdit(); }}
                  />
                ) : (() => {
                  const sms = smsFor(inv.amount, rate, inv.sms_override);
                  if (sms == null) return <span className="text-slate-300" title="No rate set for this client">—</span>;
                  return (
                    <span title={inv.sms_override != null ? "Manual override" : "Auto from rate"}>
                      {sms.toLocaleString()}
                      {inv.sms_override != null && <span className="ml-0.5 text-amber-500">*</span>}
                    </span>
                  );
                })()}
              </td>
              <td className="max-w-[24rem] text-slate-500">
                {isEditing ? (
                  <input
                    type="text" placeholder="Memo (optional)"
                    className="w-full min-w-[16rem] rounded border border-slate-300 px-2 py-1 text-xs"
                    value={draftMemo}
                    onChange={(e) => setDraftMemo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(inv); if (e.key === "Escape") cancelEdit(); }}
                  />
                ) : <span className="block truncate">{inv.memo}</span>}
              </td>
              <td><StatusPill status={inv.collection_status} /></td>
              <td className="space-x-1 pr-1 text-right whitespace-nowrap">
                {isEditing ? (
                  <>
                    <button disabled={busyId === inv.id}
                            className="chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-100"
                            onClick={() => saveEdit(inv)} title="Save amount & SMS">Save</button>
                    <button disabled={busyId === inv.id}
                            className="chip bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200"
                            onClick={cancelEdit} title="Cancel">Cancel</button>
                  </>
                ) : (
                  <>
                    {inv.collection_status === "open" && (
                      <>
                        <button disabled={busyId === inv.id}
                                className="chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-100"
                                onClick={() => setStatus(inv.id, "paid")} title="Mark as paid">Paid</button>
                        <button disabled={busyId === inv.id}
                                className="chip bg-amber-50 text-amber-700 ring-1 ring-amber-200/60 hover:bg-amber-100"
                                onClick={() => setStatus(inv.id, "ignored")} title="Mark as quotation — not a real debt">Quote</button>
                      </>
                    )}
                    {/* Closing without claiming the money arrived. Reversible with Reopen — unlike
                        Delete, which destroys the row. Offered while the invoice is still live. */}
                    {(inv.collection_status === "open" || inv.collection_status === "ignored") && (
                      <button disabled={busyId === inv.id}
                              className="chip bg-stone-100 text-stone-700 ring-1 ring-stone-300/60 hover:bg-stone-200"
                              onClick={() => setStatus(inv.id, "written_off")}
                              title="Close it without recording a payment — it leaves the open list and every total, but the record is kept">
                        Write off
                      </button>
                    )}
                    {inv.collection_status !== "open" && (
                      <button disabled={busyId === inv.id}
                              className="chip bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200"
                              onClick={() => setStatus(inv.id, "open")} title="Re-open">Reopen</button>
                    )}
                    <button disabled={busyId === inv.id}
                            className="chip bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/60 hover:bg-indigo-100"
                            onClick={() => startEdit(inv)} title="Edit amount & SMS">Edit</button>
                    <button disabled={busyId === inv.id}
                            className="chip bg-rose-50 text-rose-700 ring-1 ring-rose-200/60 hover:bg-rose-100"
                            onClick={() => remove(inv.id, inv.number)} title="Hard delete">Delete</button>
                  </>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      {!showAll && visible.length < invoices.length && (
        <div className="mt-2 text-[10px] text-slate-400">
          {invoices.length - visible.length} closed / quote invoice(s) hidden.
        </div>
      )}
    </div>
  );
}

function FilterCard({
  label, sub, value, accent, active, onClick,
}: {
  label: string; sub: string; value: string; accent: string;
  active?: boolean; onClick?: () => void;
}) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`card relative overflow-hidden p-5 text-left transition
        ${clickable ? "card-hover cursor-pointer" : "cursor-default"}
        ${active ? "ring-2 ring-indigo-500" : ""}`}
    >
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accent} opacity-70`} />
      <div className="relative">
        <div className="flex items-center gap-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          {clickable && (
            <span className={`text-[10px] font-medium ${active ? "text-indigo-600" : "text-slate-400"}`}>
              {active ? "● filtering" : "filter"}
            </span>
          )}
        </div>
        <div className="mt-2 text-2xl font-semibold tracking-tightish text-slate-900">{value}</div>
        <div className="mt-0.5 text-xs text-slate-500">{sub}</div>
      </div>
    </button>
  );
}

function StatusPill({ status }: { status: Invoice["collection_status"] }) {
  const cls = {
    open:        "pill pill-blue",
    paid:        "pill pill-green",
    ignored:     "pill pill-amber",
    written_off: "pill pill-slate",
  }[status];
  const label = status === "ignored" ? "quote" : status.replace("_", " ");
  return <span className={cls}>{label}</span>;
}

function EditDialog({
  companyId, client, onClose, onSaved,
}: {
  companyId: number;
  client: ClientRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: client?.name || "",
    contact_name: client?.contact_name || "",
    email: client?.email || "",
    language: (client?.language || "en") as "en" | "th",
    credit_terms_days: client?.credit_terms_days ?? 30,
    rate: client?.rate != null ? String(client.rate) : "",
    notes: client?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      let rate: number | null = null;
      if (form.rate.trim() !== "") {
        rate = Number(form.rate.trim());
        if (!Number.isFinite(rate) || rate <= 0) throw new Error("Rate must be a positive number (THB per SMS), or blank.");
      }
      const payload = { ...form, rate };
      if (client) {
        await api(`/api/collection/clients/${client.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      } else {
        await api("/api/collection/clients", { method: "POST", body: JSON.stringify({ ...payload, company_id: companyId }) });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!client || !confirm(`Delete ${client.name}? This also removes their invoices.`)) return;
    await api(`/api/collection/clients/${client.id}`, { method: "DELETE" });
    onSaved();
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <form onSubmit={save} className="card w-full max-w-lg space-y-4 p-6">
        <div className="text-base font-semibold tracking-tightish">{client ? `Edit ${client.name}` : "New client"}</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Contact name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Email <span className="font-normal text-slate-400">— first = primary; rest = CC. Separate with <code>,</code> or <code>;</code></span>
            </label>
            <input
              type="text"
              inputMode="email"
              className="w-full"
              value={form.email}
              placeholder="primary@client.co.th, accounting@client.co.th; manager@client.co.th"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Language</label>
            <select className="w-full" value={form.language}
                    onChange={(e) => setForm({ ...form, language: e.target.value as "en" | "th" })}>
              <option value="en">English</option>
              <option value="th">ไทย (Thai)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Credit terms (days)</label>
            <input type="number" className="w-full" min={0} max={365}
                   value={form.credit_terms_days}
                   onChange={(e) => setForm({ ...form, credit_terms_days: Number(e.target.value) })} />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              SMS rate <span className="font-normal text-slate-400">— THB per SMS; used to compute SMS = amount ÷ rate. Blank = unknown.</span>
            </label>
            <input type="number" className="w-full" min={0} step="0.0001"
                   placeholder="e.g. 0.15"
                   value={form.rate}
                   onChange={(e) => setForm({ ...form, rate: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Notes</label>
          <textarea className="w-full" rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">{err}</div>}
        <div className="flex items-center gap-2 pt-1">
          {client && <button type="button" className="btn-danger" onClick={remove}>Delete</button>}
          <div className="ml-auto flex items-center gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({
  label, value, onChange, type = "text", required,
}: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      <input type={type} className="w-full" value={value} required={required}
             onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
